"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.FileIds = exports.Fixed = exports.Files = exports.PositiveInteger = exports.MultiLineText = exports.SingleLineText = exports.Toggle = exports.SelectMultiple = exports.SelectOne = void 0;
const immutable_1 = require("immutable");
class DynamicSchemaField {
    constructor({ key, label, type }) {
        this.toJSON = () => ({
            key: this.key,
            label: this.label,
            type: this.type,
        });
        this.key = key;
        this.label = label;
        this.type = type;
    }
}
DynamicSchemaField.fromJSON = (json) => {
    const obj = Object.assign({}, json);
    const { key, label, type } = obj;
    if (typeof key !== 'string')
        throw new Error('.key must be string');
    if (typeof label !== 'string')
        throw new Error('.label must be a string');
    if (typeof type !== 'string')
        throw new Error('.type must be a string');
    if (type === 'SELECT_ONE') {
        return SelectOne.fromJSON(obj);
    }
    if (type === 'SELECT_MULTIPLE') {
        return SelectMultiple.fromJSON(obj);
    }
    if (type === 'TOGGLE') {
        return Toggle.fromJSON(obj);
    }
    if (type === 'SINGLE_LINE_TEXT') {
        return SingleLineText.fromJSON(obj);
    }
    if (type === 'MULTI_LINE_TEXT') {
        return MultiLineText.fromJSON(obj);
    }
    if (type === 'POSITIVE_INTEGER') {
        return PositiveInteger.fromJSON(obj);
    }
    if (type === 'FIXED') {
        return Fixed.fromJSON(obj);
    }
    if (type === 'FILES') {
        return Files.fromJSON(obj);
    }
    if (type === 'FILE_IDS') {
        return FileIds.fromJSON(obj);
    }
    throw new Error(`A creative brief field type "${type}" is not valid type.`);
};
class SelectOne extends DynamicSchemaField {
    constructor({ key, label, choices, helperText, }) {
        super({ key, label, type: 'SELECT_ONE' });
        this.getDefaultValue = () => this.choices.first().key;
        this.getAdaptedValue = (value) => this.choices.find(choice => choice.key === value) ? value : this.getDefaultValue();
        this.toJSON = () => ({
            key: this.key,
            label: this.label,
            type: this.type,
            choices: this.choices.toJSON(),
            helperText: this.helperText,
        });
        this.choices = choices;
        this.helperText = helperText;
    }
}
exports.SelectOne = SelectOne;
SelectOne.fromJSON = (json) => {
    const { key, label, choices, helperText } = Object.assign({}, json);
    if (!(Array.isArray(choices) || immutable_1.List.isList(choices)))
        throw new Error('.label must be an array of choices');
    if (choices.every(item => typeof item.key === 'string' && item.label === 'string'))
        throw new Error('.label must be an array of choices');
    return new SelectOne({
        key,
        label,
        choices: immutable_1.List(choices),
        helperText: helperText || '',
    });
};
class SelectMultiple extends DynamicSchemaField {
    constructor({ key, label, choices, helperText, isRequiredToSelectAnything, }) {
        super({ key, label, type: 'SELECT_MULTIPLE' });
        this.getDefaultValue = () => immutable_1.Set();
        this.getAdaptedValue = (value) => {
            if (value instanceof immutable_1.Set) {
                return value.filter(item => this.choices.find(choice => choice.key === item));
            }
            return this.getDefaultValue();
        };
        this.toJSON = () => ({
            key: this.key,
            label: this.label,
            type: this.type,
            choices: this.choices.toJSON(),
            isRequiredToSelectAnything: this.isRequiredToSelectAnything,
        });
        this.choices = choices;
        this.helperText = helperText;
        this.isRequiredToSelectAnything = isRequiredToSelectAnything;
    }
}
exports.SelectMultiple = SelectMultiple;
SelectMultiple.fromJSON = (json) => {
    const { key, label, choices, helperText, isRequiredToSelectAnything } = Object.assign({}, json);
    if (!(Array.isArray(choices) || immutable_1.List.isList(choices))) {
        throw new Error('.label must be an array of choices');
    }
    if (choices.every(item => typeof item.key === 'string' && item.label === 'string')) {
        throw new Error('.label must be an array of choices');
    }
    if (typeof isRequiredToSelectAnything !== 'boolean') {
        throw new Error('.isRequiredToSelectAnything must be a boolean');
    }
    return new SelectMultiple({
        key,
        label,
        choices: immutable_1.List(choices),
        helperText: helperText || '',
        isRequiredToSelectAnything,
    });
};
class Toggle extends DynamicSchemaField {
    constructor({ key, label, checkedControlLabel, uncheckedControlLabel, }) {
        super({ key, label, type: 'TOGGLE' });
        this.getDefaultValue = () => false;
        this.getAdaptedValue = (value) => (value === true ? value : this.getDefaultValue());
        this.toJSON = () => ({
            key: this.key,
            label: this.label,
            type: this.type,
            checkedControlLabel: this.checkedControlLabel,
            uncheckedControlLabel: this.uncheckedControlLabel,
        });
        this.checkedControlLabel = checkedControlLabel;
        this.uncheckedControlLabel = uncheckedControlLabel;
    }
}
exports.Toggle = Toggle;
Toggle.fromJSON = (json) => {
    const { key, label, checkedControlLabel, uncheckedControlLabel } = Object.assign({}, json);
    if (typeof checkedControlLabel !== 'string') {
        throw new Error('.checkedControlLabel must be a string');
    }
    if (typeof uncheckedControlLabel !== 'string') {
        throw new Error('.uncheckedControlLabel must be a string');
    }
    return new Toggle({
        key,
        label,
        checkedControlLabel,
        uncheckedControlLabel,
    });
};
class SingleLineText extends DynamicSchemaField {
    constructor({ key, label, helperText, isRequired, placeholder, }) {
        super({ key, label, type: 'SINGLE_LINE_TEXT' });
        this.getDefaultValue = () => '';
        this.getAdaptedValue = (value) => typeof value === 'string' && !value.includes('\n') ? value : this.getDefaultValue();
        this.toJSON = () => ({
            key: this.key,
            label: this.label,
            type: this.type,
            helperText: this.helperText,
            isRequired: this.isRequired,
            placeholder: this.placeholder,
        });
        this.helperText = helperText;
        this.isRequired = isRequired;
        this.placeholder = placeholder;
    }
}
exports.SingleLineText = SingleLineText;
SingleLineText.fromJSON = (json) => {
    const { key, label, helperText, isRequired, placeholder } = Object.assign({}, json);
    if (typeof isRequired !== 'boolean') {
        throw new Error('.isRequired must be a boolean');
    }
    if (typeof placeholder !== 'string') {
        throw new Error('.placeholder must be a string');
    }
    return new SingleLineText({
        key,
        label,
        helperText: helperText || '',
        isRequired,
        placeholder,
    });
};
class MultiLineText extends DynamicSchemaField {
    constructor({ key, label, helperText, isRequired, placeholder, }) {
        super({ key, label, type: 'MULTI_LINE_TEXT' });
        this.getDefaultValue = () => '';
        this.getAdaptedValue = (value) => typeof value === 'string' ? value : this.getDefaultValue();
        this.toJSON = () => ({
            key: this.key,
            label: this.label,
            type: this.type,
            helperText: this.helperText,
            isRequired: this.isRequired,
            placeholder: this.placeholder,
        });
        this.helperText = helperText;
        this.isRequired = isRequired;
        this.placeholder = placeholder;
    }
}
exports.MultiLineText = MultiLineText;
MultiLineText.fromJSON = (json) => {
    const { key, label, helperText, isRequired, placeholder } = Object.assign({}, json);
    if (typeof isRequired !== 'boolean') {
        throw new Error('.isRequired must be a boolean');
    }
    if (typeof placeholder !== 'string') {
        throw new Error('.placeholder must be a string');
    }
    return new MultiLineText({
        key,
        label,
        helperText: helperText || '',
        isRequired,
        placeholder,
    });
};
class PositiveInteger extends DynamicSchemaField {
    constructor({ key, label, helperText, isRequired, }) {
        super({ key, label, type: 'POSITIVE_INTEGER' });
        this.getDefaultValue = () => null;
        this.getAdaptedValue = (value) => Number.isSafeInteger(value) && value >= 0 ? value : this.getDefaultValue();
        this.toJSON = () => ({
            key: this.key,
            label: this.label,
            type: this.type,
            helperText: this.helperText,
            isRequired: this.isRequired,
        });
        this.helperText = helperText;
        this.isRequired = isRequired;
    }
}
exports.PositiveInteger = PositiveInteger;
PositiveInteger.fromJSON = (json) => {
    const { key, label, helperText, isRequired } = Object.assign({}, json);
    if (typeof isRequired !== 'boolean') {
        throw new Error('.isRequired must be a boolean');
    }
    return new PositiveInteger({
        key,
        label,
        helperText: helperText || '',
        isRequired,
    });
};
class Files extends DynamicSchemaField {
    constructor({ key, label, helperText }) {
        super({ key, label, type: 'FILES' });
        this.getDefaultValue = () => null;
        this.getAdaptedValue = (value) => typeof value === 'string' || value === null ? value : this.getDefaultValue();
        this.helperText = helperText;
    }
}
exports.Files = Files;
Files.fromJSON = (json) => {
    const { key, label, helperText } = Object.assign({}, json);
    return new Files({
        key,
        label,
        helperText,
    });
};
class Fixed extends DynamicSchemaField {
    constructor({ key, label, value }) {
        super({ key, label, type: 'FIXED' });
        this.getDefaultValue = () => this.value;
        this.getAdaptedValue = (_) => this.getDefaultValue();
        this.toJSON = () => ({
            key: this.key,
            label: this.label,
            value: this.value,
            type: this.type,
        });
        this.value = value;
    }
}
exports.Fixed = Fixed;
Fixed.fromJSON = (json) => {
    const { key, label, value } = Object.assign({}, json);
    if (typeof value !== 'string') {
        throw new Error('.value must be a string');
    }
    return new Fixed({
        key,
        label,
        value,
    });
};
class FileIds extends DynamicSchemaField {
    constructor({ key, label }) {
        super({ key, label, type: 'FILE_IDS' });
        this.getDefaultValue = () => [];
        this.getAdaptedValue = (value) => (value == null ? this.getDefaultValue() : value);
    }
}
exports.FileIds = FileIds;
FileIds.fromJSON = (json) => {
    const { key, label } = Object.assign({}, json);
    return new FileIds({
        key,
        label,
    });
};
exports.default = DynamicSchemaField;
